// Reusable file attachment input component for incident reporting
// CREATED: Handles file selection, preview, and removal before submission
// UPDATED: Integrated "+" tile in preview grid for seamless file addition
// Features:
// - Desktop: Single button "Ajouter une pièce jointe" with Paperclip icon (shown only when no files)
// - Mobile: Single button "Joindre une photo" with Camera icon (shown only when no files)
// - Once files added: "+" tile integrated in preview grid for adding more files
// - Preview grid with thumbnails for images and icons for PDFs
// - File validation (size, MIME type, count) with human-friendly error messages
// - Remove files before upload (X button always visible on mobile)
// - Progressive counter: visible from 1st file, turns amber at limit with "limite atteinte" text
// - Supports all image formats including HEIC/HEIF (iPhone), up to 10 MB per file

import { useRef, useState } from 'react';
import { Paperclip, Camera, X, FileText, AlertCircle, Plus } from 'lucide-react';
import {
  MAX_FILE_SIZE,
  MAX_FILES_COUNT,
  ALLOWED_MIME_TYPES,
  validateFiles,
  isImageFile,
  isPdfFile,
  type FileValidationError,
} from '../../lib/attachments';

interface FileAttachmentInputProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  maxFiles?: number;
  maxSizeBytes?: number;
  acceptedTypes?: string[];
  disabled?: boolean;
  isMobile?: boolean;
}

export function FileAttachmentInput({
  files,
  onFilesChange,
  maxFiles = MAX_FILES_COUNT,
  maxSizeBytes = MAX_FILE_SIZE,
  acceptedTypes = ALLOWED_MIME_TYPES,
  disabled = false,
  isMobile = false,
}: FileAttachmentInputProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [validationErrors, setValidationErrors] = useState<FileValidationError[]>([]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    // Combine with existing files
    const allFiles = [...files, ...selectedFiles];

    // Validate
    const { valid, errors } = validateFiles(allFiles);

    setValidationErrors(errors);

    // Only add valid files
    if (valid.length > 0) {
      onFilesChange(valid.slice(0, maxFiles));
    }

    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    onFilesChange(newFiles);

    // Clear errors if any
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const createPreviewUrl = (file: File): string => {
    return URL.createObjectURL(file);
  };

  const canAddMore = files.length < maxFiles;
  const showCounter = files.length > 0;
  const isAtLimit = files.length >= maxFiles;

  return (
    <div className="space-y-3">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf"
        multiple
        onChange={handleFileSelect}
        disabled={disabled}
        className="hidden"
      />

      {/* Single button (mobile or desktop) - shown only when no files or disabled */}
      {(files.length === 0 || disabled) && (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          className={`w-full px-4 ${isMobile ? 'py-3 min-h-[48px] rounded-xl' : 'py-2 rounded-lg'} transition-all duration-200 flex items-center justify-center gap-2 text-sm font-medium ${
            disabled
              ? 'bg-gray-50 border border-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:border-gray-300 text-gray-700'
          }`}
        >
          {isMobile ? (
            <>
              <Camera size={18} />
              <span>Joindre une photo</span>
            </>
          ) : (
            <>
              <Paperclip size={18} />
              <span>Ajouter une pièce jointe</span>
            </>
          )}
        </button>
      )}

      {/* File counter - visible from first file, amber when limit reached */}
      {showCounter && (
        <div className={`text-xs ${isAtLimit ? 'font-medium text-amber-600' : 'text-gray-500'}`}>
          {files.length} / {maxFiles} fichier{files.length > 1 ? 's' : ''}
          {isAtLimit && ' - limite atteinte'}
        </div>
      )}

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          {validationErrors.map((error, index) => (
            <div key={index} className="flex items-start gap-2 text-sm text-red-700">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error.error}</span>
            </div>
          ))}
        </div>
      )}

      {/* Preview grid */}
      {files.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {files.map((file, index) => {
            const isImage = isImageFile(file.type);
            const isPdf = isPdfFile(file.type);
            const previewUrl = isImage ? createPreviewUrl(file) : '';

            return (
              <div
                key={index}
                className="relative group aspect-square rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-50"
              >
                {/* Preview */}
                {isImage ? (
                  <img
                    src={previewUrl}
                    alt={file.name}
                    className="w-full h-full object-cover"
                    onLoad={() => URL.revokeObjectURL(previewUrl)}
                  />
                ) : isPdf ? (
                  <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center">
                    <FileText size={32} className="text-red-500 mb-1" />
                    <span className="text-xs text-gray-600 line-clamp-2 break-all">
                      {file.name}
                    </span>
                  </div>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center">
                    <FileText size={32} className="text-gray-400 mb-1" />
                    <span className="text-xs text-gray-600 line-clamp-2 break-all">
                      {file.name}
                    </span>
                  </div>
                )}

                {/* Remove button - always visible on mobile, hover on desktop */}
                <button
                  type="button"
                  onClick={() => handleRemoveFile(index)}
                  disabled={disabled}
                  className={`absolute top-1 right-1 p-1 bg-red-500 hover:bg-red-600 text-white rounded-full transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                    isMobile ? 'opacity-90' : 'opacity-0 group-hover:opacity-100'
                  }`}
                  aria-label="Supprimer le fichier"
                >
                  <X size={14} />
                </button>

              </div>
            );
          })}

          {/* Add button tile - integrated in grid when files present */}
          {canAddMore && !disabled && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="aspect-square rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-gray-400 transition-all duration-200 flex flex-col items-center justify-center gap-1 text-gray-600 hover:text-gray-700"
            >
              <Plus size={24} strokeWidth={2} />
              <span className="text-xs font-medium">Ajouter</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
